import { useCallback, useContext, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Line, LineChart, ResponsiveContainer, Tooltip } from "recharts";
import api from "../api";
import { ReactComponent as QualificationIcon } from "../assets/images/icons/ic-bag.svg";
import { ReactComponent as EmailIcon } from "../assets/images/icons/ic-email.svg";
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg";
import { ReactComponent as BioIcon } from "../assets/images/icons/ic-knowledge.svg";
import { ReactComponent as LogoutIcon } from "../assets/images/icons/ic-logout.svg";
import { ReactComponent as SkillsIcon } from "../assets/images/icons/ic-skills.svg";
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png";
import { Each } from "../common/Each";
import MainContext from "../common/MainContext";
import Button from "../components/Button";
import EditProfile from "../components/EditProfile";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import Tag from "../components/Tag";
import { auth } from "../firebase";
import styles from "./Profile.module.css";
import Skeleton from "../components/Skeleton";

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const context = useContext(MainContext)
  const [showEditPage, setShowEditPage] = useState(false)
  const [tags, setTags] = useState([])
  const [periodDays, setPeriodDays] = useState(30)
  const [loadingStats, setLoadingStats] = useState(false)
  const [stats, setStats] = useState({
    period_days: 30,
    lessons_done: 0,
    effective_lesson_seconds: 0,
    average_session_seconds: 0,
    average_students_per_session: 0,
    students_in_period: 0,
    average_attendance_rate_percent: 0,
    attendance_rate_trend_by_day: [],
    average_participation_seconds: 0,
    returning_students_rate_percent: 0,
    early_dropout_rate_percent: 0,
    early_dropout_rate_trend_by_day: [],
    lesson_feedbacks_count: 0,
    positive_feedbacks_count: 0,
    neutral_feedbacks_count: 0,
    negative_feedbacks_count: 0,
    average_lesson_feedback_score: null,
    average_lesson_feedback_score_by_question: [],
    worst_lessons_feedback: [],
    feedback_trend_by_day: [],
    average_start_delta_seconds: 0,
    average_end_delta_seconds: 0,
  })

  const formatDuration = useCallback((seconds) => {
    const totalSeconds = Math.max(0, Math.round(Number(seconds || 0)))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const remainingSeconds = totalSeconds % 60

    return `${hours}h ${minutes}m ${remainingSeconds}s`
  }, [])

  const formatDeltaMinutes = useCallback((seconds) => {
    const safeSeconds = Number(seconds || 0)
    const minutes = Math.round(Math.abs(safeSeconds) / 60)

    if (safeSeconds > 0) {
      return `${minutes} min in ritardo`
    }

    if (safeSeconds < 0) {
      return `${minutes} min in anticipo`
    }

    return "in orario"
  }, [])

  const formatPercent = useCallback((value) => {
    const safeValue = Number(value || 0)
    return `${safeValue.toFixed(1)}%`
  }, [])

  const formatScore = useCallback((value) => {
    if (value === null || value === undefined) return "-"
    return `${Number(value).toFixed(1)} / 5`
  }, [])

  const getScoreClass = useCallback((value) => {
    const score = Number(value)
    if (!Number.isFinite(score)) return ""

    if (score > 4) return styles.scoreHigh
    if (score >= 3) return styles.scoreMedium
    return styles.scoreLow
  }, [])

  const formatDayLabel = useCallback((value) => {
    if (!value) return ""
    const date = new Date(`${value}T00:00:00`)
    return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })
  }, [])

  const formatLongDayLabel = useCallback((value) => {
    if (!value) return ""
    const date = new Date(`${value}T00:00:00`)
    return date.toLocaleDateString("it-IT", { day: "numeric", month: "long" })
  }, [])

  const enrichSeriesWithMovingAverage = useCallback((series, key, windowSize) => {
    const source = series ?? []
    return source.map((item, index) => {
      if (index + 1 < windowSize) {
        return {
          ...item,
          moving_average: null,
        }
      }

      const windowValues = source
        .slice(index - windowSize + 1, index + 1)
        .map((entry) => Number(entry?.[key]))

      if (windowValues.some((value) => !Number.isFinite(value))) {
        return {
          ...item,
          moving_average: null,
        }
      }

      const average = windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length
      return {
        ...item,
        moving_average: average,
      }
    })
  }, [])

  const getTrendDirection = useCallback((series, key, epsilon = 0.12, windowSize = 3) => {
    const source = series ?? []
    if (source.length === 0) return null

    // If the latest day has no value, trend is stale/missing and must not be inferred.
    const lastValue = Number(source[source.length - 1]?.[key])
    if (!Number.isFinite(lastValue)) return null

    const values = (series ?? [])
      .map((item) => Number(item?.[key]))
      .filter((value) => Number.isFinite(value))

    if (values.length < (windowSize + 1)) return null

    const smoothed = []
    for (let i = 0; i <= values.length - windowSize; i += 1) {
      const windowValues = values.slice(i, i + windowSize)
      const avg = windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length
      smoothed.push(avg)
    }

    if (smoothed.length < 2) return null

    const n = smoothed.length
    let sumX = 0
    let sumY = 0
    let sumXY = 0
    let sumXX = 0

    smoothed.forEach((value, index) => {
      sumX += index
      sumY += value
      sumXY += index * value
      sumXX += index * index
    })

    const denominator = (n * sumXX) - (sumX * sumX)
    if (denominator === 0) return null

    const slope = ((n * sumXY) - (sumX * sumY)) / denominator

    if (Math.abs(slope) <= epsilon) return "flat"
    return slope > 0 ? "up" : "down"
  }, [])

  useEffect(() => {
    const getTags = async () => {
      try {
        const res = await api.get("/teacher/tags")
        setTags(res)
      } catch (e) {
        console.error(e)
      }
    }

    getTags()
  }, [])

  useEffect(() => {
    const getProfileStatistics = async () => {
      setLoadingStats(true)
      try {
        const response = await api.get(`/teacher/profile/statistics?days=${periodDays}`)
        setStats(response)
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingStats(false)
      }
    }

    getProfileStatistics()
  }, [periodDays])

  useEffect(() => {
    window.scrollTo({
      top: 0,
    })
  }, [showEditPage])

  const logout = async () => {
    await auth.signOut()
    context.setUser(null)
    navigate("/login")
  }

  const feedbackTrendData = (stats.feedback_trend_by_day ?? []).map((item) => ({
    ...item,
    // A day with no feedback should not count as a score=0 data point.
    average_score: Number(item?.feedback_count || 0) > 0 ? Number(item?.average_score) : null,
    label: formatDayLabel(item.date),
  }))

  const attendanceTrendData = (stats.attendance_rate_trend_by_day ?? []).map((item) => ({
    ...item,
    label: formatDayLabel(item.date),
  }))

  const dropoutTrendData = (stats.early_dropout_rate_trend_by_day ?? []).map((item) => ({
    ...item,
    label: formatDayLabel(item.date),
  }))

  const trendWindowSize = periodDays <= 7 ? 3 : periodDays <= 30 ? 7 : 15

  const feedbackTrendChartData = enrichSeriesWithMovingAverage(feedbackTrendData, "average_score", trendWindowSize)
  const attendanceTrendChartData = enrichSeriesWithMovingAverage(attendanceTrendData, "average_rate_percent", trendWindowSize)
  const dropoutTrendChartData = enrichSeriesWithMovingAverage(dropoutTrendData, "average_rate_percent", trendWindowSize)

  const attendanceTrendDirection = getTrendDirection(attendanceTrendData, "average_rate_percent", 0.1, trendWindowSize)
  const dropoutTrendDirection = getTrendDirection(dropoutTrendData, "average_rate_percent", 0.1, trendWindowSize)
  const feedbackTrendDirection = getTrendDirection(feedbackTrendData, "average_score", 0.05, trendWindowSize)

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>
            Profilo
          </title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          {
            showEditPage && <EditProfile onBack={() => setShowEditPage(false)} tagsList={tags} />
          }

          {
            !showEditPage &&
            <div className={styles.sectionInner}>
              <div className={styles.profileContainer}>
                <div className={styles.profile}>
                  <div className={styles.teacher}>
                    {
                      context.user?.picture &&
                      <img className={styles.profilePic} src={context.user?.picture} alt='' />
                    }
                    {
                      !context.user &&
                      <img className={styles.profilePic} src={UserPlaceholder} alt='' />
                    }
                    {
                      !context.user &&
                      <Skeleton type="rect" width="164px" height="24px" borderRadius={"12px"} />
                    }
                    {
                      context.user &&
                      <div className={styles.username}>{`${context.user?.name} ${context.user?.surname}`}</div>
                    }
                    <Button
                      disabled={!context.user}
                      onClick={() => {
                        setShowEditPage(true)
                      }}
                    >
                      <EditIcon />
                      {t("profile.edit").toUpperCase()}
                    </Button>
                  </div>
                  <div className={styles.teacherInfo}>
                    <div className={styles.attribute}>
                      <div className={styles.label}>
                        <EmailIcon className={styles.profileIcon} />
                        {t("profile.email")}
                      </div>
                      <div className={styles.attributeValue}>{context.user?.email}</div>
                    </div>
                    <div className={styles.attribute}>
                      <div className={styles.label}>
                        <QualificationIcon className={styles.profileIcon} />
                        {t("profile.qualification")}
                      </div>
                      <div className={styles.attributeValue}>{context.user?.qualification}</div>
                    </div>
                    <div className={styles.attribute}>
                      <div className={styles.label}>
                        <BioIcon className={styles.profileIcon} />
                        {t("profile.bio")}
                      </div>
                      <div className={styles.attributeValueFullWidth}>{context.user?.description}</div>
                    </div>
                    <div className={styles.attribute}>
                      <div className={styles.label}>
                        <SkillsIcon className={styles.profileIcon} />
                        {t("profile.skills")}
                      </div>
                      <div className={styles.attributeValueFullWidth}>
                        <Each
                          of={context.user?.tags ?? []}
                          render={(tag) => (
                            <Tag tag={tag} />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={styles.logout}
                    onClick={logout}
                  >
                    <LogoutIcon />
                    Logout
                  </div>
                </div>
              </div>
              <div className={styles.statsContainer}>
                <div className={styles.card}>
                  <div className={styles.header}>
                    <div className={styles.subtitle}>{t("profile.statistics.title").toUpperCase()}</div>
                  </div>

                  <div className={styles.filterRow}>
                    <Each
                      of={[7, 30, 90]}
                      render={(days) => (
                        <button
                          className={`${styles.filterButton} ${periodDays === days ? styles.active : ""}`}
                          onClick={() => setPeriodDays(days)}
                        >
                          {days}g
                        </button>
                      )}
                    />
                  </div>

                  {
                    loadingStats === true &&
                    <div className={styles.metricsGrid}>
                      <Each
                        of={[1, 2, 3, 4, 5, 6, 7, 8]}
                        render={() => (
                          <div className={styles.metricCardSkeleton}>
                            <Skeleton type="rect" width="100%" height="56px" borderRadius={"8px"} />
                          </div>
                        )}
                      />
                    </div>
                  }

                  {
                    loadingStats === false &&
                    <div className={styles.statsSections}>
                      <div className={styles.statsSection}>
                        <div className={styles.sectionTitle}>Lezioni</div>
                        <div className={styles.metricsGrid}>
                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Lezioni effettuate</div>
                            <div className={styles.metricValue}>{stats.lessons_done ?? 0}</div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Ore di lezione effettive</div>
                            <div className={styles.metricValue}>{formatDuration(stats.effective_lesson_seconds)}</div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Media delle sessioni</div>
                            <div className={styles.metricValue}>{formatDuration(stats.average_session_seconds)}</div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Scostamento medio inizio lezione</div>
                            <div className={styles.metricValue}>{formatDeltaMinutes(stats.average_start_delta_seconds)}</div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Scostamento medio fine lezione</div>
                            <div className={styles.metricValue}>{formatDeltaMinutes(stats.average_end_delta_seconds)}</div>
                          </div>
                        </div>
                      </div>

                      <div className={styles.statsSection}>
                        <div className={styles.sectionTitle}>Studenti</div>
                        <div className={styles.metricsGrid}>
                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Numero medio studenti/sessione</div>
                            <div className={styles.metricValue}>{stats.average_students_per_session ?? 0}</div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Studenti nel periodo</div>
                            <div className={styles.metricValue}>{stats.students_in_period ?? 0}</div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Tasso abbandono anticipato (&lt;80%)</div>
                            <div className={styles.metricValueWithTrend}>
                              <span className={styles.metricValue}>{formatPercent(stats.early_dropout_rate_percent)}</span>
                              {
                                dropoutTrendDirection === "up" &&
                                <span className={`${styles.trendIndicator} ${styles.trendDown}`}>
                                  <span className={styles.trendArrow}>↑</span>
                                  <span className={styles.trendLabel}>in aumento</span>
                                </span>
                              }
                              {
                                dropoutTrendDirection === "down" &&
                                <span className={`${styles.trendIndicator} ${styles.trendUp}`}>
                                  <span className={styles.trendArrow}>↓</span>
                                  <span className={styles.trendLabel}>in calo</span>
                                </span>
                              }
                              {
                                dropoutTrendDirection === "flat" &&
                                <span className={`${styles.trendIndicator} ${styles.trendNeutral}`}>
                                  <span className={styles.trendArrow}>→</span>
                                  <span className={styles.trendLabel}>stabile</span>
                                </span>
                              }
                            </div>
                            {
                              dropoutTrendChartData.length > 0 &&
                              <div className={styles.feedbackMiniChartContainer}>
                                <ResponsiveContainer width="100%" height={74}>
                                  <LineChart data={dropoutTrendChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                    <Tooltip
                                      content={({ active, payload }) => {
                                        if (!active || !payload || payload.length === 0) {
                                          return null
                                        }

                                        const item = payload[0]?.payload
                                        if (!item) {
                                          return null
                                        }

                                        const valueText = Number.isFinite(Number(item.average_rate_percent))
                                          ? `${Number(item.average_rate_percent).toFixed(2)}%`
                                          : "-"

                                        return (
                                          <div
                                            style={{
                                              backgroundColor: "#ffffff",
                                              border: "1px solid rgba(0, 0, 0, 0.12)",
                                              borderRadius: "8px",
                                              padding: "8px 10px",
                                              fontSize: "12px",
                                              lineHeight: 1.35,
                                            }}
                                          >
                                            <div style={{ fontWeight: 700, textTransform: "capitalize" }}>
                                              {formatLongDayLabel(item.date)}
                                            </div>
                                            <div>{item.lessons_count} lezioni</div>
                                            <div>Tasso: {valueText}</div>
                                          </div>
                                        )
                                      }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="average_rate_percent"
                                      stroke="#1f6feb"
                                      strokeWidth={2.2}
                                      dot={false}
                                      connectNulls={true}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="moving_average"
                                      stroke="#f59e0b"
                                      strokeWidth={2}
                                      strokeDasharray="4 3"
                                      dot={false}
                                      connectNulls={true}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                                <div className={styles.chartLegend}>
                                  <span className={styles.chartLegendItem}>
                                    <span className={styles.chartLegendLine} />
                                    Andamento
                                  </span>
                                  <span className={styles.chartLegendItem}>
                                    <span className={`${styles.chartLegendLine} ${styles.chartLegendLineMovingAverage}`} />
                                    Media mobile
                                  </span>
                                </div>
                              </div>
                            }
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Presenza media per lezione</div>
                            <div className={styles.metricValueWithTrend}>
                              <span className={styles.metricValue}>{formatPercent(stats.average_attendance_rate_percent)}</span>
                              {
                                attendanceTrendDirection === "up" &&
                                <span className={`${styles.trendIndicator} ${styles.trendUp}`}>
                                  <span className={styles.trendArrow}>↑</span>
                                  <span className={styles.trendLabel}>in aumento</span>
                                </span>
                              }
                              {
                                attendanceTrendDirection === "down" &&
                                <span className={`${styles.trendIndicator} ${styles.trendDown}`}>
                                  <span className={styles.trendArrow}>↓</span>
                                  <span className={styles.trendLabel}>in calo</span>
                                </span>
                              }
                              {
                                attendanceTrendDirection === "flat" &&
                                <span className={`${styles.trendIndicator} ${styles.trendNeutral}`}>
                                  <span className={styles.trendArrow}>→</span>
                                  <span className={styles.trendLabel}>stabile</span>
                                </span>
                              }
                            </div>
                            {
                              attendanceTrendChartData.length > 0 &&
                              <div className={styles.feedbackMiniChartContainer}>
                                <ResponsiveContainer width="100%" height={74}>
                                  <LineChart data={attendanceTrendChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                    <Tooltip
                                      content={({ active, payload }) => {
                                        if (!active || !payload || payload.length === 0) {
                                          return null
                                        }

                                        const item = payload[0]?.payload
                                        if (!item) {
                                          return null
                                        }

                                        const valueText = Number.isFinite(Number(item.average_rate_percent))
                                          ? `${Number(item.average_rate_percent).toFixed(2)}%`
                                          : "-"

                                        return (
                                          <div
                                            style={{
                                              backgroundColor: "#ffffff",
                                              border: "1px solid rgba(0, 0, 0, 0.12)",
                                              borderRadius: "8px",
                                              padding: "8px 10px",
                                              fontSize: "12px",
                                              lineHeight: 1.35,
                                            }}
                                          >
                                            <div style={{ fontWeight: 700, textTransform: "capitalize" }}>
                                              {formatLongDayLabel(item.date)}
                                            </div>
                                            <div>{item.lessons_count} lezioni</div>
                                            <div>Presenza: {valueText}</div>
                                          </div>
                                        )
                                      }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="average_rate_percent"
                                      stroke="#1f6feb"
                                      strokeWidth={2.2}
                                      dot={false}
                                      connectNulls={true}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="moving_average"
                                      stroke="#f59e0b"
                                      strokeWidth={2}
                                      strokeDasharray="4 3"
                                      dot={false}
                                      connectNulls={true}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                                <div className={styles.chartLegend}>
                                  <span className={styles.chartLegendItem}>
                                    <span className={styles.chartLegendLine} />
                                    Andamento
                                  </span>
                                  <span className={styles.chartLegendItem}>
                                    <span className={`${styles.chartLegendLine} ${styles.chartLegendLineMovingAverage}`} />
                                    Media mobile
                                  </span>
                                </div>
                              </div>
                            }
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Tempo medio di permanenza per studente</div>
                            <div className={styles.metricValue}>{formatDuration(stats.average_participation_seconds)}</div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Ritorno studenti</div>
                            <div className={styles.metricValue}>{formatPercent(stats.returning_students_rate_percent)}</div>
                          </div>
                        </div>
                      </div>

                      <div className={styles.statsSection}>
                        <div className={styles.sectionTitle}>Feedback</div>
                        <div className={styles.metricsGrid}>
                          <div className={`${styles.metricCard} ${styles.feedbackTotalsCard}`}>
                            <div className={styles.metricLabel}>Feedback lezioni</div>
                            <div className={styles.feedbackTotalsGrid}>
                              <div className={styles.feedbackTotalsItem}>
                                <div className={styles.feedbackTotalsLabel}>Totali</div>
                                <div className={styles.feedbackTotalsValue}>{stats.lesson_feedbacks_count ?? 0}</div>
                              </div>
                              <div className={styles.feedbackTotalsItem}>
                                <div className={`${styles.feedbackTotalsLabel} ${styles.feedbackTotalsLabelPositive}`}>Positivi</div>
                                <div className={`${styles.feedbackTotalsValue} ${styles.scoreHigh}`}>{stats.positive_feedbacks_count ?? 0}</div>
                              </div>
                              <div className={styles.feedbackTotalsItem}>
                                <div className={`${styles.feedbackTotalsLabel} ${styles.feedbackTotalsLabelNeutral}`}>Neutri</div>
                                <div className={`${styles.feedbackTotalsValue} ${styles.scoreMedium}`}>{stats.neutral_feedbacks_count ?? 0}</div>
                              </div>
                              <div className={styles.feedbackTotalsItem}>
                                <div className={`${styles.feedbackTotalsLabel} ${styles.feedbackTotalsLabelNegative}`}>Negativi</div>
                                <div className={`${styles.feedbackTotalsValue} ${styles.scoreLow}`}>{stats.negative_feedbacks_count ?? 0}</div>
                              </div>
                            </div>
                          </div>

                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Punteggio Medio</div>
                            <div className={styles.metricValueWithTrend}>
                              <span className={`${styles.metricValue} ${getScoreClass(stats.average_lesson_feedback_score)}`}>
                                {formatScore(stats.average_lesson_feedback_score)}
                              </span>
                              {
                                feedbackTrendDirection === "up" &&
                                <span className={`${styles.trendIndicator} ${styles.trendUp}`}>
                                  <span className={styles.trendArrow}>↑</span>
                                  <span className={styles.trendLabel}>in aumento</span>
                                </span>
                              }
                              {
                                feedbackTrendDirection === "down" &&
                                <span className={`${styles.trendIndicator} ${styles.trendDown}`}>
                                  <span className={styles.trendArrow}>↓</span>
                                  <span className={styles.trendLabel}>in calo</span>
                                </span>
                              }
                              {
                                feedbackTrendDirection === "flat" &&
                                <span className={`${styles.trendIndicator} ${styles.trendNeutral}`}>
                                  <span className={styles.trendArrow}>→</span>
                                  <span className={styles.trendLabel}>stabile</span>
                                </span>
                              }
                            </div>

                            {
                              feedbackTrendChartData.length > 0 &&
                              <div className={styles.feedbackMiniChartContainer}>
                                <ResponsiveContainer width="100%" height={74}>
                                  <LineChart data={feedbackTrendChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                    <Tooltip
                                      content={({ active, payload }) => {
                                        if (!active || !payload || payload.length === 0) {
                                          return null
                                        }

                                        const item = payload[0]?.payload
                                        if (!item) {
                                          return null
                                        }

                                        const averageText = Number.isFinite(Number(item.average_score))
                                          ? `${Number(item.average_score).toFixed(2)} / 5`
                                          : "-"

                                        return (
                                          <div
                                            style={{
                                              backgroundColor: "#ffffff",
                                              border: "1px solid rgba(0, 0, 0, 0.12)",
                                              borderRadius: "8px",
                                              padding: "8px 10px",
                                              fontSize: "12px",
                                              lineHeight: 1.35,
                                            }}
                                          >
                                            <div style={{ fontWeight: 700, textTransform: "capitalize" }}>
                                              {formatLongDayLabel(item.date)}
                                            </div>
                                            <div>{item.feedback_count} feedback</div>
                                            <div>Media: {averageText}</div>
                                          </div>
                                        )
                                      }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="average_score"
                                      stroke="#1f6feb"
                                      strokeWidth={2.2}
                                      dot={false}
                                      connectNulls={true}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="moving_average"
                                      stroke="#f59e0b"
                                      strokeWidth={2}
                                      strokeDasharray="4 3"
                                      dot={false}
                                      connectNulls={true}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                                <div className={styles.chartLegend}>
                                  <span className={styles.chartLegendItem}>
                                    <span className={styles.chartLegendLine} />
                                    Andamento
                                  </span>
                                  <span className={styles.chartLegendItem}>
                                    <span className={`${styles.chartLegendLine} ${styles.chartLegendLineMovingAverage}`} />
                                    Media mobile
                                  </span>
                                </div>
                              </div>
                            }

                            {
                              (stats.average_lesson_feedback_score_by_question?.length ?? 0) > 0 &&
                              <div className={styles.feedbackQuestionsColumn}>
                                <div className={styles.feedbackQuestionsTitle}>Media voti per domanda (1-5)</div>
                                <Each
                                  of={stats.average_lesson_feedback_score_by_question}
                                  render={(item) => (
                                    <div className={styles.questionCard}>
                                      <div className={styles.questionText}>{item.question}</div>
                                      <div className={styles.questionMeta}>
                                        <span className={getScoreClass(item.average_score)}>{formatScore(item.average_score)}</span>
                                        <span>{item.answers_count} risposte</span>
                                      </div>
                                    </div>
                                  )}
                                />
                              </div>
                            }

                          </div>
                        </div>

                        {
                          (stats.worst_lessons_feedback?.length ?? 0) > 0 &&
                          <div className={styles.metricCard}>
                            <div className={styles.metricLabel}>Lezioni Peggiori</div>
                            <div className={styles.worstLessonsCompactList}>
                              <Each
                                of={stats.worst_lessons_feedback}
                                render={(lesson) => (
                                  <div className={styles.worstLessonCompactItem}>
                                    <div className={styles.worstLessonCompactTop}>
                                      <span className={styles.worstLessonCompactName}>{lesson.lesson_name}</span>
                                      <span className={`${styles.worstLessonScore} ${getScoreClass(lesson.average_score)}`}>
                                        {formatScore(lesson.average_score)}
                                      </span>
                                    </div>
                                    <div className={styles.worstLessonMeta}>
                                      {lesson.lesson_date ? `${formatLongDayLabel(lesson.lesson_date)} - ` : ""}{lesson.feedbacks_count} feedback
                                    </div>
                                    <Each
                                      of={lesson.average_score_by_question ?? []}
                                      render={(question) => (
                                        <div className={styles.worstLessonQuestionRow}>
                                          <span className={styles.worstLessonQuestionLabel}>{question.question}</span>
                                          <span className={getScoreClass(question.average_score)}>{formatScore(question.average_score)}</span>
                                        </div>
                                      )}
                                    />
                                  </div>
                                )}
                              />
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>

    </HeaderFooterLayout>

  )
}


export default Profile
